/**
 * Cookie and storage consent in the web app (core compliance/cookieconsent.ts; the website's banner is apps/landing/site/consent.js).
 * The app keeps only necessary storage (sign-in, your saved work, settings you set in your account). Analytics, when a build sends
 * any, waits for a yes: `gated(send)` drops every call before a yes, after "Reject non-essential", and under GPC or Do Not Track.
 */
import { measureRouter, type AnonymousEvent } from "@logwave/core/src/compliance/measure.ts";
import { COOKIE_CONSENT_KEY, bannerNeeded, categoryAllowed, makeChoice, parseChoice, type CookieChoice, type CookieVia, type PrivacySignals } from "@logwave/core/src/compliance/cookieconsent.ts";

/**
 * This build sends no LINKED analytics (main, 2026-10-08; integ/usage-consent adds them). A build that does sets this true: the banner
 * and the fun ask then show. Anonymous counts (core measure.ts) need no ask and run in every real build.
 */
export const ANALYTICS_IN_BUILD = false;

const listeners = new Set<(c: CookieChoice) => void>();

export function signals(nav: Partial<Navigator> & { globalPrivacyControl?: boolean } = typeof navigator === "undefined" ? {} : navigator): PrivacySignals {
  const dnt = (nav as { doNotTrack?: string | null }).doNotTrack;
  return { gpc: nav.globalPrivacyControl === true, dnt: dnt === "1" || dnt === "yes" };
}

export function readChoice(): CookieChoice | null {
  try { return parseChoice(localStorage.getItem(COOKIE_CONSENT_KEY)); } catch { return null; }
}

export const showBanner = (): boolean => bannerNeeded(readChoice(), signals());
export const analyticsAllowed = (): boolean => categoryAllowed(readChoice(), "analytics", signals());

const newId = () => { const a = new Uint8Array(16); crypto.getRandomValues(a); return [...a].map((b) => b.toString(16).padStart(2, "0")).join(""); };

/** Saves a choice (necessary storage) and tells listeners. `record` logs it on the server for a signed-in person (usage_data). */
export async function saveChoice(via: CookieVia, picked: { analytics?: boolean } = {}, record?: (analytics: boolean) => Promise<unknown>): Promise<CookieChoice> {
  const prev = readChoice();
  const c = makeChoice(via, { preferences: false, ...picked }, prev?.id ?? newId(), new Date().toISOString());
  try { localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(c)); } catch { /* private mode: asked again next time */ }
  for (const l of listeners) l(c);
  if (record) await record(c.analytics).catch(() => undefined); // the local choice already counts
  return c;
}

export function onConsentChange(fn: (c: CookieChoice) => void): () => void { listeners.add(fn); return () => listeners.delete(fn); }

/** Wraps an analytics sender: nothing is sent unless the person said yes (checked at each call, so Reject stops it at once). */
export function gated<A extends unknown[]>(send: (...a: A) => void): (...a: A) => void {
  return (...a: A) => { if (analyticsAllowed()) send(...a); };
}

/** Posts anonymous events (no cookie, no id; the server only counts). Never under Do Not Track or GPC. Injected in tests. */
export function anonSender(post: (body: string) => void): (e: AnonymousEvent) => void {
  return (e) => { const s = signals(); if (s.gpc || s.dnt) return; post(JSON.stringify({ events: [e] })); };
}

/**
 * The one line a sender wires (core measureRouter): anonymous by default, linked only while the person said yes.
 *   const measure = makeMeasure(postAnon, sendLinked);  measure({ surface: "web", kind: "view", path, width }, linkedExtras)
 */
export const makeMeasure = <L,>(postAnon: (body: string) => void, linked?: (e: AnonymousEvent, extra: L) => void) =>
  measureRouter<L>({ linkedAllowed: analyticsAllowed, anonymous: anonSender(postAnon), ...(linked ? { linked } : {}) });
